import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, message,
  Card, Tag, Space, Popconfirm, Tabs, Row, Col, InputNumber,
  DatePicker, Divider, Collapse
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, EyeOutlined,
  CopyOutlined, RocketOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import moment from 'moment';

const { TabPane } = Tabs;
const { Option } = Select;
const { Panel } = Collapse;
const { TextArea } = Input;

const CampaignTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [targetingTemplates, setTargetingTemplates] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [adSetForms, setAdSetForms] = useState([{ key: 0 }]);
  const [adSetKeyCounter, setAdSetKeyCounter] = useState(1);
  const [form] = Form.useForm();
  const isInitialEditLoad = React.useRef(false);

  // 获取模板列表
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/campaign-templates/');
      setTemplates(response.data);
    } catch (error) {
      message.error('获取模板列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取定向模板
  const fetchTargetingTemplates = async () => {
    try {
      const response = await api.get('/targeting-templates/');
      setTargetingTemplates(response.data);
    } catch (error) {
      message.error('获取定向模板失败');
    }
  };

  // 获取素材列表
  const fetchMaterials = async () => {
    try {
      const response = await api.get('/materials/', { params: { page: 1, page_size: 100 } });
      const data = response.data;
      setMaterials(Array.isArray(data) ? data : (data?.items || []));
    } catch (error) {
      message.error('获取素材列表失败');
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchTargetingTemplates();
    fetchMaterials();
  }, []);

  // Effect 1: 处理模态框打开/关闭，设置 Campaign 值和 adSetForms
  useEffect(() => {
    if (!modalVisible) {
      isInitialEditLoad.current = false;
      setAdSetForms([{ key: 0 }]);
      setAdSetKeyCounter(1);
      form.resetFields();
      return;
    }

    if (!editingTemplate) {
      // 创建新模板 - 确保表单完全清空
      isInitialEditLoad.current = false;
      form.resetFields();
      setAdSetForms([{ key: 0 }]);
      setAdSetKeyCounter(1);
      return;
    }

    // 编辑现有模板
    isInitialEditLoad.current = true;
    const adsets = editingTemplate.adset_templates || [];
    const newAdSetForms = adsets.map((_, i) => ({ key: i }));
    setAdSetForms(newAdSetForms.length > 0 ? newAdSetForms : [{ key: 0 }]);
    setAdSetKeyCounter(newAdSetForms.length > 0 ? newAdSetForms.length : 1);

    // Campaign 级别的字段立即回填（这些字段始终存在）
    const campaignValues = {
      name: editingTemplate.name,
      description: editingTemplate.description,
      objective: editingTemplate.objective,
      daily_budget: editingTemplate.daily_budget,
      bid_strategy: editingTemplate.bid_strategy,
      buying_type: editingTemplate.buying_type,
      message_template: editingTemplate.message_template,
      headline_template: editingTemplate.headline_template,
      call_to_action: editingTemplate.call_to_action,
      link_url: editingTemplate.link_url,
      material_ids: editingTemplate.material_ids || [],
      targeting_template_id: editingTemplate.targeting_template_id || undefined,
      start_time: editingTemplate.start_time ? moment(editingTemplate.start_time) : null,
      end_time: editingTemplate.end_time ? moment(editingTemplate.end_time) : null,
    };
    form.setFieldsValue(campaignValues);
  }, [editingTemplate, modalVisible, form]);

  // Effect 2: 当 adSetForms 变化且处于编辑初始加载时，回填 AdSet 字段
  // 此 effect 在 React 渲染完新的动态 Form.Item 后执行，确保字段已注册
  useEffect(() => {
    if (!isInitialEditLoad.current || !editingTemplate || !modalVisible) return;

    const adsets = editingTemplate.adset_templates || [];
    if (adsets.length === 0 || adSetForms.length === 0) {
      isInitialEditLoad.current = false;
      return;
    }

    // 只有 adSetForms 和模板 adsets 数量一致时才回填（防止用户手动增删时触发）
    if (adsets.length !== adSetForms.length) {
      isInitialEditLoad.current = false;
      return;
    }

    const adSetValues = {};
    adsets.forEach((adset, index) => {
      const key = adSetForms[index].key;
      adSetValues[`adset_name_${key}`] = adset.name;
      adSetValues[`adset_daily_budget_${key}`] = adset.daily_budget !== null && adset.daily_budget !== undefined ? Number(adset.daily_budget) : 10;
      adSetValues[`adset_bid_amount_${key}`] = adset.bid_amount !== null && adset.bid_amount !== undefined ? Number(adset.bid_amount) : undefined;
      adSetValues[`adset_bid_strategy_${key}`] = adset.bid_strategy;
      adSetValues[`adset_billing_event_${key}`] = adset.billing_event;
      adSetValues[`adset_optimization_goal_${key}`] = adset.optimization_goal;
      adSetValues[`adset_age_min_${key}`] = adset.targeting_config?.age_min ?? 18;
      adSetValues[`adset_age_max_${key}`] = adset.targeting_config?.age_max ?? 65;
      adSetValues[`adset_countries_${key}`] = adset.targeting_config?.geo_locations?.countries || ['US'];
      adSetValues[`adset_genders_${key}`] = adset.targeting_config?.genders ?? [0, 1];
      adSetValues[`adset_custom_audiences_${key}`] = adset.custom_audience_config?.include || [];
      adSetValues[`adset_excluded_audiences_${key}`] = adset.custom_audience_config?.exclude || [];
      adSetValues[`adset_platforms_${key}`] = adset.placement_config?.publisher_platforms || ['facebook', 'instagram'];
      adSetValues[`adset_start_time_${key}`] = adset.start_time ? moment(adset.start_time) : null;
      adSetValues[`adset_end_time_${key}`] = adset.end_time ? moment(adset.end_time) : null;
    });

    form.setFieldsValue(adSetValues);
    isInitialEditLoad.current = false;
  }, [adSetForms, editingTemplate, modalVisible, form]);

  // 创建/更新模板
  const handleSubmit = async (values) => {
    try {
      // 构建 adset_templates（使用 adSet.key 读取表单值，避免删除后 index 错乱）
      const adsetTemplates = adSetForms.map((adSet) => {
        const key = adSet.key;
        return {
          name: values[`adset_name_${key}`],
          daily_budget: values[`adset_daily_budget_${key}`],
          bid_amount: values[`adset_bid_amount_${key}`],
          bid_strategy: values[`adset_bid_strategy_${key}`],
          billing_event: values[`adset_billing_event_${key}`],
          optimization_goal: values[`adset_optimization_goal_${key}`],
          targeting_config: {
            geo_locations: {
              countries: values[`adset_countries_${key}`] || ['US']
            },
            age_min: values[`adset_age_min_${key}`] ?? 18,
            age_max: values[`adset_age_max_${key}`] ?? 65,
            genders: values[`adset_genders_${key}`] ?? [0, 1],
          },
          custom_audience_config: {
            include: values[`adset_custom_audiences_${key}`] || [],
            exclude: values[`adset_excluded_audiences_${key}`] || []
          },
          placement_config: {
            publisher_platforms: values[`adset_platforms_${key}`] || ['facebook', 'instagram']
          },
          start_time: values[`adset_start_time_${key}`]?.toISOString?.() || values[`adset_start_time_${key}`],
          end_time: values[`adset_end_time_${key}`]?.toISOString?.() || values[`adset_end_time_${key}`],
        };
      });

      const payload = {
        ...values,
        adset_templates: adsetTemplates,
        start_time: values.start_time?.toISOString(),
        end_time: values.end_time?.toISOString(),
      };

      if (editingTemplate) {
        await api.put(`/campaign-templates/${editingTemplate.id}`, payload);
        message.success('更新成功');
      } else {
        await api.post('/campaign-templates/', payload);
        message.success('创建成功');
      }

      setModalVisible(false);
      form.resetFields();
      setAdSetForms([{ key: 0 }]);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      message.error(error.response?.data?.detail || '操作失败');
    }
  };

  // 删除模板
  const handleDelete = async (id) => {
    try {
      await api.delete(`/campaign-templates/${id}`);
      message.success('删除成功');
      fetchTemplates();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 预览模板
  const handlePreview = async (template) => {
    try {
      const response = await api.get(`/campaign-templates/${template.id}/preview`);
      setPreviewData(response.data);
      setPreviewVisible(true);
    } catch (error) {
      message.error('获取预览失败');
    }
  };

  // 添加 AdSet 表单
  const addAdSetForm = () => {
    setAdSetForms([...adSetForms, { key: adSetKeyCounter }]);
    setAdSetKeyCounter(adSetKeyCounter + 1);
  };

  // 删除 AdSet 表单
  const removeAdSetForm = (index) => {
    setAdSetForms(adSetForms.filter((_, i) => i !== index));
  };

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '广告目标',
      dataIndex: 'objective',
      key: 'objective',
      render: (objective) => <Tag color="blue">{objective}</Tag>,
    },
    {
      title: '日预算',
      dataIndex: 'daily_budget',
      key: 'daily_budget',
      render: (budget) => `$${budget}`,
    },
    {
      title: 'AdSet 数量',
      key: 'adset_count',
      render: (_, record) => record.adset_templates?.length || 0,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '正常' : '停用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
          >
            预览
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditingTemplate(record);
              setModalVisible(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除吗？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="系列模板"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingTemplate(null);
              form.resetFields();
              setAdSetForms([{ key: 0 }]);
              setAdSetKeyCounter(1);
              setModalVisible(true);
            }}
          >
            创建模板
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
        />
      </Card>

      {/* 创建/编辑模态框 */}
      <Modal
        title={editingTemplate ? '编辑模板' : '创建模板'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false);
          setEditingTemplate(null);
          form.resetFields();
          setAdSetForms([{ key: 0 }]);
          setAdSetKeyCounter(1);
        }}
        width={900}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
        >
          <Tabs defaultActiveKey="campaign">
            <TabPane tab="Campaign 设置" key="campaign">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="模板名称"
                    rules={[{ required: true, message: '请输入模板名称' }]}
                  >
                    <Input placeholder="输入模板名称" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="objective"
                    label="广告目标"
                    initialValue="OUTCOME_TRAFFIC"
                  >
                    <Select>
                      <Option value="OUTCOME_TRAFFIC">流量</Option>
                      <Option value="OUTCOME_SALES">销售</Option>
                      <Option value="OUTCOME_LEADS">潜在客户</Option>
                      <Option value="OUTCOME_AWARENESS">品牌认知</Option>
                      <Option value="OUTCOME_ENGAGEMENT">互动</Option>
                      <Option value="OUTCOME_APP_PROMOTION">应用推广</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="description"
                label="描述"
              >
                <TextArea rows={2} placeholder="模板描述" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="daily_budget"
                    label="日预算 (USD)"
                    initialValue={10}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="bid_strategy"
                    label="出价策略"
                    initialValue="LOWEST_COST_WITHOUT_CAP"
                  >
                    <Select>
                      <Option value="LOWEST_COST_WITHOUT_CAP">最低成本</Option>
                      <Option value="COST_CAP">成本上限</Option>
                      <Option value="LOWEST_COST_WITH_BID_CAP">出价上限</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="buying_type"
                    label="购买类型"
                    initialValue="AUCTION"
                  >
                    <Select>
                      <Option value="AUCTION">竞拍</Option>
                      <Option value="RESERVED">预留</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="start_time"
                    label="开始时间"
                  >
                    <DatePicker showTime style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="end_time"
                    label="结束时间"
                  >
                    <DatePicker showTime style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>广告文案</Divider>

              <Form.Item
                name="message_template"
                label="广告正文"
              >
                <TextArea rows={3} placeholder="广告正文模板" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="headline_template"
                    label="标题"
                  >
                    <Input placeholder="广告标题" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="call_to_action"
                    label="行动号召"
                    initialValue="SHOP_NOW"
                  >
                    <Select>
                      <Option value="SHOP_NOW">立即购买</Option>
                      <Option value="LEARN_MORE">了解更多</Option>
                      <Option value="SIGN_UP">注册</Option>
                      <Option value="DOWNLOAD">下载</Option>
                      <Option value="BOOK_TRAVEL">预订行程</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="link_url"
                label="落地页 URL"
              >
                <Input placeholder="https://example.com" />
              </Form.Item>

              <Form.Item
                name="material_ids"
                label="选择素材"
              >
                <Select mode="multiple" placeholder="选择素材">
                  {materials.map(m => (
                    <Option key={m.id} value={m.id}>{m.name}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="targeting_template_id"
                label="定向模板"
              >
                <Select placeholder="选择定向模板">
                  {targetingTemplates.map(t => (
                    <Option key={t.id} value={t.id}>{t.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </TabPane>

            <TabPane tab="AdSet 设置" key="adsets">
              {adSetForms.map((adSet, index) => (
                <Card
                  key={adSet.key}
                  title={`AdSet ${index + 1}`}
                  extra={
                    adSetForms.length > 1 && (
                      <Button
                        danger
                        size="small"
                        onClick={() => removeAdSetForm(index)}
                      >
                        删除
                      </Button>
                    )
                  }
                  style={{ marginBottom: 16 }}
                >
                  <Form.Item
                    name={`adset_name_${adSet.key}`}
                    label="AdSet 名称"
                    rules={[{ required: true, message: '请输入 AdSet 名称' }]}
                  >
                    <Input placeholder="例如：AdSet 1 - 美国" />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        name={`adset_daily_budget_${adSet.key}`}
                        label="日预算"
                        initialValue={10}
                      >
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name={`adset_bid_amount_${adSet.key}`}
                        label="出价金额"
                      >
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name={`adset_bid_strategy_${adSet.key}`}
                        label="出价策略"
                        initialValue="LOWEST_COST_WITHOUT_CAP"
                      >
                        <Select>
                          <Option value="LOWEST_COST_WITHOUT_CAP">最低成本</Option>
                          <Option value="COST_CAP">成本上限</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name={`adset_billing_event_${adSet.key}`}
                        label="计费事件"
                        initialValue="IMPRESSIONS"
                      >
                        <Select>
                          <Option value="IMPRESSIONS">展示</Option>
                          <Option value="LINK_CLICKS">链接点击</Option>
                          <Option value="APP_INSTALLS">应用安装</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={`adset_optimization_goal_${adSet.key}`}
                        label="优化目标"
                        initialValue="LINK_CLICKS"
                      >
                        <Select>
                          <Option value="LINK_CLICKS">链接点击</Option>
                          <Option value="IMPRESSIONS">展示</Option>
                          <Option value="REACH">覆盖人数</Option>
                          <Option value="OFFSITE_CONVERSIONS">转化</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider>定向设置</Divider>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name={`adset_age_min_${adSet.key}`}
                        label="最小年龄"
                        initialValue={18}
                      >
                        <InputNumber min={13} max={65} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={`adset_age_max_${adSet.key}`}
                        label="最大年龄"
                        initialValue={65}
                      >
                        <InputNumber min={13} max={65} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name={`adset_countries_${adSet.key}`}
                    label="国家"
                    initialValue={['US']}
                  >
                    <Select mode="multiple" placeholder="选择国家">
                      <Option value="US">美国</Option>
                      <Option value="CA">加拿大</Option>
                      <Option value="GB">英国</Option>
                      <Option value="AU">澳大利亚</Option>
                      <Option value="DE">德国</Option>
                      <Option value="FR">法国</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name={`adset_genders_${adSet.key}`}
                    label="性别"
                    initialValue={[0, 1]}
                  >
                    <Select mode="multiple">
                      <Option value={0}>女性</Option>
                      <Option value={1}>男性</Option>
                    </Select>
                  </Form.Item>

                  <Divider>自定义受众</Divider>

                  <Form.Item
                    name={`adset_custom_audiences_${adSet.key}`}
                    label="包含受众"
                  >
                    <Select mode="multiple" placeholder="选择自定义受众">
                      <Option value="123">Website Visitors</Option>
                      <Option value="456">App Users</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name={`adset_excluded_audiences_${adSet.key}`}
                    label="排除受众"
                  >
                    <Select mode="multiple" placeholder="选择排除受众">
                      <Option value="789">Purchasers</Option>
                    </Select>
                  </Form.Item>

                  <Divider>版位</Divider>

                  <Form.Item
                    name={`adset_platforms_${adSet.key}`}
                    label="投放平台"
                    initialValue={['facebook', 'instagram']}
                  >
                    <Select mode="multiple">
                      <Option value="facebook">Facebook</Option>
                      <Option value="instagram">Instagram</Option>
                      <Option value="audience_network">Audience Network</Option>
                      <Option value="messenger">Messenger</Option>
                    </Select>
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name={`adset_start_time_${adSet.key}`}
                        label="开始时间"
                      >
                        <DatePicker showTime style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={`adset_end_time_${adSet.key}`}
                        label="结束时间"
                      >
                        <DatePicker showTime style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}

              <Button
                type="dashed"
                onClick={addAdSetForm}
                block
                icon={<PlusOutlined />}
              >
                添加 AdSet
              </Button>
            </TabPane>
          </Tabs>
        </Form>
      </Modal>

      {/* 预览模态框 */}
      <Modal
        title="模板预览"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        footer={null}
      >
        {previewData && (
          <div>
            <Card title="Campaign 配置" size="small">
              <p><strong>名称：</strong>{previewData.campaign.name}</p>
              <p><strong>目标：</strong>{previewData.campaign.objective}</p>
              <p><strong>预算：</strong>${previewData.campaign.daily_budget}</p>
              <p><strong>出价策略：</strong>{previewData.campaign.bid_strategy}</p>
            </Card>

            <Divider />

            <Collapse>
              {previewData.adsets.map((adset, index) => (
                <Panel header={`AdSet ${index + 1}: ${adset.name}`} key={index}>
                  <p><strong>预算：</strong>${adset.daily_budget || adset.lifetime_budget}</p>
                  <p><strong>出价：</strong>${adset.bid_amount}</p>
                  <p><strong>优化目标：</strong>{adset.optimization_goal}</p>
                  <p><strong>计费事件：</strong>{adset.billing_event}</p>
                  
                  <Divider orientation="left">定向</Divider>
                  <pre>{JSON.stringify(adset.targeting, null, 2)}</pre>
                </Panel>
              ))}
            </Collapse>

            {previewData.creatives && (
              <>
                <Divider />
                <Card title="广告创意" size="small">
                  {previewData.creatives.map((creative, index) => (
                    <div key={index}>
                      <p><strong>正文：</strong>{creative.object_story_spec?.link_data?.message}</p>
                      <p><strong>标题：</strong>{creative.object_story_spec?.link_data?.name}</p>
                      <p><strong>链接：</strong>{creative.object_story_spec?.link_data?.link}</p>
                    </div>
                  ))}
                </Card>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CampaignTemplates;
